"use client"

import { useState, useEffect } from "react"
import { supabase } from "../../supabaseClient"
import { useAuth } from "../../context/AuthContext"
import { applyInstitutionFilter, ensureInstitutionOnPayload } from "../../lib/tenant"
import "../../styles/TurmaForm.css"

export default function TurmaForm({ onAdded, turmaParaEditar, onCancelEdit }) {
  const { isAdmin, instituicaoId } = useAuth()
  const [nome, setNome] = useState("")
  const [ano, setAno] = useState("1")
  const [salaId, setSalaId] = useState("")
  const [semestre, setSemestre] = useState("")
  const [trimestre, setTrimestre] = useState("")
  const [turno, setTurno] = useState("manha")
  const [periodo, setPeriodo] = useState("Regular")
  const [classe, setClasse] = useState("")
  const [cursoId, setCursoId] = useState("")
  const [salas, setSalas] = useState([])
  const [cursos, setCursos] = useState([])
  const [disciplinas, setDisciplinas] = useState([])
  const [disciplinasSelecionadas, setDisciplinasSelecionadas] = useState({})
  const [loading, setLoading] = useState(false)
  const [isEditing, setIsEditing] = useState(false)

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [salasRes, cursosRes, discRes] = await Promise.all([
          applyInstitutionFilter(supabase.from("salas").select("id, nome").order("nome"), isAdmin, instituicaoId),
          applyInstitutionFilter(supabase.from("cursos").select("id, nome").order("nome"), isAdmin, instituicaoId),
          applyInstitutionFilter(
            supabase.from("disciplinas").select("id, nome, codigo, tipo, importancia").order("nome"),
            isAdmin,
            instituicaoId,
          ),
        ])
        setSalas(salasRes.data || [])
        setCursos(cursosRes.data || [])
        setDisciplinas(discRes.data || [])
      } catch (err) {
        console.error("Erro ao carregar dados:", err.message)
      }
    }
    fetchData()
  }, [])

  useEffect(() => {
    if (turmaParaEditar) {
      setIsEditing(true)
      setNome(turmaParaEditar.nome || "")
      setAno(String(turmaParaEditar.ano || "1"))
      setSalaId(turmaParaEditar.sala_id || "")
      setSemestre(turmaParaEditar.semestre || "")
      setTrimestre(turmaParaEditar.trimestre || "")
      setTurno(turmaParaEditar.turno === "manhã" ? "manha" : turmaParaEditar.turno || "manha")
      setPeriodo(turmaParaEditar.periodo || "Regular")
      setClasse(turmaParaEditar.classe || "")
      setCursoId(turmaParaEditar.curso_id || "")
      carregarDisciplinasTurma(turmaParaEditar.id)
    } else {
      resetForm()
    }
  }, [turmaParaEditar])

  const carregarDisciplinasTurma = async (turmaId) => {
    try {
      const { data, error } = await supabase
        .from("turma_disciplina")
        .select("disciplina_id, tempos_semanais")
        .eq("turma_id", turmaId)
      if (error) throw error

      const disciplinasMap = {}
      data.forEach((d) => {
        disciplinasMap[d.disciplina_id] = d.tempos_semanais || 4
      })
      setDisciplinasSelecionadas(disciplinasMap)
    } catch (err) {
      console.error("Erro ao carregar disciplinas da turma:", err.message)
    }
  }

  const resetForm = () => {
    setIsEditing(false)
    setNome("")
    setAno("1")
    setSalaId("")
    setSemestre("")
    setTrimestre("")
    setTurno("manha")
    setPeriodo("Regular")
    setClasse("")
    setCursoId("")
    setDisciplinasSelecionadas({})
  }

  const toggleDisciplina = (discId) => {
    setDisciplinasSelecionadas((prev) => {
      if (prev[discId] !== undefined) {
        const newState = { ...prev }
        delete newState[discId]
        return newState
      } else {
        const disc = disciplinas.find((d) => d.id === discId)
        const temposPadrao = disc?.tipo === "N" ? 6 : 4
        return { ...prev, [discId]: temposPadrao }
      }
    })
  }

  const alterarTemposSemanais = (discId, tempos) => {
    setDisciplinasSelecionadas((prev) => ({
      ...prev,
      [discId]: Number.parseInt(tempos) || 4,
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!nome.trim()) {
      alert("Preencha o nome da turma!")
      return
    }

    const disciplinasIds = Object.keys(disciplinasSelecionadas)
    const totalTempos = disciplinasIds.reduce((acc, discId) => {
      return acc + (disciplinasSelecionadas[discId] || 0)
    }, 0)

    const LIMITE_TEMPOS = 30 // 6 tempos por dia x 5 dias

    if (totalTempos > LIMITE_TEMPOS) {
      alert(
        `Total de tempos excede o limite!\n\n` +
          `Total atual: ${totalTempos} tempos\n` +
          `Limite máximo: ${LIMITE_TEMPOS} tempos (6 por dia x 5 dias)\n\n` +
          `Precisa reduzir ${totalTempos - LIMITE_TEMPOS} tempos para adicionar.`,
      )
      return
    }

    setLoading(true)

    try {
      const turnoNormalizado = turno === "manhã" ? "manha" : turno

      const turmaData = {
        nome,
        ano: Number(ano) || 1,
        sala_id: salaId || null,
        semestre: semestre || null,
        trimestre: trimestre || null,
        turno: turnoNormalizado,
        periodo: periodo || "Regular",
        classe: classe || null,
        curso_id: cursoId || null,
      }

      let turmaId

      if (isEditing && turmaParaEditar) {
        const { error: updateError } = await supabase.from("turmas").update(turmaData).eq("id", turmaParaEditar.id)

        if (updateError) throw updateError
        turmaId = turmaParaEditar.id

        await supabase.from("turma_disciplina").delete().eq("turma_id", turmaId)
      } else {
        const payload = ensureInstitutionOnPayload({ ...turmaData, created_at: new Date().toISOString() }, isAdmin, instituicaoId)
        const { data: novaTurma, error: insertError } = await supabase
          .from("turmas")
          .insert([payload])
          .select("id")
          .single()

        if (insertError) throw insertError
        turmaId = novaTurma.id
      }

      for (const discId of disciplinasIds) {
        try {
          await supabase.from("turma_disciplina").insert({
            turma_id: turmaId,
            disciplina_id: discId,
            tempos_semanais: disciplinasSelecionadas[discId],
          })
        } catch (insertErr) {
          // Ignorar erros de duplicata
          console.log("Disciplina já existe, ignorando:", discId)
        }
      }

      resetForm()
      if (isEditing && onCancelEdit) onCancelEdit()
      if (onAdded) onAdded()
    } catch (err) {
      alert("Erro ao salvar turma: " + err.message)
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const handleCancelar = () => {
    resetForm()
    if (onCancelEdit) onCancelEdit()
  }

  const disciplinasSelecionadasCount = Object.keys(disciplinasSelecionadas).length

  return (
    <form onSubmit={handleSubmit} className="turma-form-card">
      <h2 className="form-title">{isEditing ? "Editar Turma" : "Adicionar Nova Turma"}</h2>

      <div className="form-grid">
        <div className="form-group">
          <label>
            Nome da Turma <span className="required">*</span>
          </label>
          <input
            type="text"
            placeholder="Ex: Turma A"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            className="form-input"
            required
          />
        </div>

        <div className="form-group">
          <label>Ano</label>
          <select value={ano} onChange={(e) => setAno(e.target.value)} className="form-input">
            <option value="1">1º Ano</option>
            <option value="2">2º Ano</option>
            <option value="3">3º Ano</option>
            <option value="4">4º Ano</option>
            <option value="5">5º Ano</option>
          </select>
        </div>

        <div className="form-group">
          <label>Curso</label>
          <select value={cursoId} onChange={(e) => setCursoId(e.target.value)} className="form-input">
            <option value="">Selecione o curso</option>
            {cursos.map((curso) => (
              <option key={curso.id} value={curso.id}>
                {curso.nome}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Sala</label>
          <select value={salaId} onChange={(e) => setSalaId(e.target.value)} className="form-input">
            <option value="">Selecione a sala</option>
            {salas.map((sala) => (
              <option key={sala.id} value={sala.id}>
                {sala.nome}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Turno</label>
          <select value={turno} onChange={(e) => setTurno(e.target.value)} className="form-input">
            <option value="manha">Manhã</option>
            <option value="tarde">Tarde</option>
            <option value="noite">Noite</option>
          </select>
        </div>

        <div className="form-group">
          <label>Período</label>
          <select value={periodo} onChange={(e) => setPeriodo(e.target.value)} className="form-input">
            <option value="Regular">Regular</option>
            <option value="Pos-Laboral">Pós-Laboral</option>
            <option value="Intensivo">Intensivo</option>
          </select>
        </div>

        <div className="form-group">
          <label>Semestre</label>
          <select value={semestre} onChange={(e) => setSemestre(e.target.value)} className="form-input">
            <option value="">Selecione</option>
            <option value="1">1º Semestre</option>
            <option value="2">2º Semestre</option>
          </select>
        </div>

        <div className="form-group">
          <label>Classe</label>
          <input
            type="text"
            placeholder="Ex: 10ª"
            value={classe}
            onChange={(e) => setClasse(e.target.value)}
            className="form-input"
          />
        </div>
      </div>

      <div className="form-group disciplinas-section">
        <label>Disciplinas e Tempos Semanais</label>
        <p className="helper-text">Selecione as disciplinas e defina quantos tempos semanais cada uma terá</p>
        <div className="disciplinas-grid-tempos">
          {disciplinas.length === 0 ? (
            <p className="no-items-msg">Nenhuma disciplina cadastrada.</p>
          ) : (
            disciplinas.map((d) => {
              const isSelected = disciplinasSelecionadas[d.id] !== undefined
              const tempos = disciplinasSelecionadas[d.id] || 4
              return (
                <div key={d.id} className={`disciplina-item-tempos ${isSelected ? "selected" : ""}`}>
                  <label className="disciplina-checkbox-tempos">
                    <input type="checkbox" checked={isSelected} onChange={() => toggleDisciplina(d.id)} />
                    <span className="checkbox-label-tempos">
                      <span className="disc-nome">{d.nome}</span>
                      <span className={`disc-tipo ${d.tipo === "N" ? "nuclear" : "global"}`}>
                        {d.tipo === "N" ? "Nuclear" : "Global"}
                      </span>
                    </span>
                  </label>
                  {isSelected && (
                    <div className="tempos-selector">
                      <label>Tempos:</label>
                      <select
                        value={tempos}
                        onChange={(e) => alterarTemposSemanais(d.id, e.target.value)}
                        className="tempos-input"
                      >
                        <option value="2">2 tempos</option>
                        <option value="3">3 tempos</option>
                        <option value="4">4 tempos</option>
                        <option value="5">5 tempos</option>
                        <option value="6">6 tempos</option>
                        <option value="8">8 tempos</option>
                        <option value="10">10 tempos</option>
                      </select>
                    </div>
                  )}
                </div>
              )
            })
          )}
        </div>
        <p className="selected-count">{disciplinasSelecionadasCount} disciplina(s) selecionada(s)</p>
      </div>

      <div className="form-actions">
        {isEditing && (
          <button type="button" className="btn-cancelar" onClick={handleCancelar}>
            Cancelar
          </button>
        )}
        <button type="submit" className="btn-salvar" disabled={loading}>
          {loading ? "Salvando..." : isEditing ? "Atualizar" : "Salvar"}
        </button>
      </div>
    </form>
  )
}
